import React, { useEffect, useMemo, useState } from 'react';
import './ShippingIntegrations.css';
import {
  FaPlus, FaEdit, FaTrash, FaPlug, FaEye, FaCloud, FaServer, FaCheckCircle, FaTimesCircle, FaSave, FaTimes, FaInfoCircle, FaTruck
} from 'react-icons/fa';
import { SiUps, SiFedex } from 'react-icons/si';
import { METHOD_ABBREV_REFERENCE, normalizeAbbreviationList } from '../data/shippingMethodAbbreviations';

const API = import.meta.env.VITE_API_URL;

function BrandIcon({ type }) {
  const t = String(type || '').toLowerCase();
  if (t.includes('ups')) return <SiUps className="ship-icon ups" title="UPS" />;
  if (t.includes('fedex')) return <SiFedex className="ship-icon fedex" title="FedEx" />;
  if (t.includes('ehub')) return <FaCloud className="ship-icon ehub" title="eHub" />;
  if (t.includes('shipstation')) return <FaServer className="ship-icon shipstation" title="ShipStation" />;
  if (t.includes('stamps')) return <FaServer className="ship-icon stamps" title="Stamps.com" />;
  if (t.includes('shipengine')) return <FaCloud className="ship-icon shipengine" title="ShipEngine" />;
  if (t.includes('unishippers')) return <FaTruck className="ship-icon generic" title="Unishippers" />;
  return <FaServer className="ship-icon generic" title="Provider" />;
}

const LS_SORT_KEY = 'shippingintegrations.sort.v1';

function getInitialSort() {
  try {
    const raw = localStorage.getItem(LS_SORT_KEY);
    if (!raw) return { field: 'displayname', order: 'ASC' };
    const j = JSON.parse(raw);
    const field = j?.field || 'displayname';
    const order = j?.order === 'DESC' ? 'DESC' : 'ASC';
    return { field, order };
  } catch {
    return { field: 'displayname', order: 'ASC' };
  }
}

/* ========================= Test Modal ========================= */
function TestConnectionModal({ open, onClose, status, title, detail }) {
  if (!open) return null;
  const isLoading = status?.state === 'loading';
  const isSuccess = status?.state === 'success';
  const isError = status?.state === 'error';

  return (
    <div className="ship-modal-overlay" onClick={() => !isLoading && onClose()}>
      <div className="ship-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="ship-modal-titlebar">
          <h3 className="ship-modal-title">{title || 'Testing Connection'}</h3>
          <button className="icon-btn" aria-label="Close" onClick={onClose} disabled={isLoading}>×</button>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 2px', minHeight: 60 }}>
          {isLoading && (
            <>
              <div className="loader-dot" />
              <div className="loader-dot" style={{ animationDelay: '0.1s' }} />
              <div className="loader-dot" style={{ animationDelay: '0.2s' }} />
              <div style={{ fontStyle:'italic', opacity:.9 }}>Contacting provider…</div>
            </>
          )}
          {isSuccess && (
            <>
              <FaCheckCircle size={20} style={{ color: '#22c55e' }}/>
              <div>
                <div style={{ fontWeight: 600 }}>Connection OK</div>
                {detail ? <div style={{ opacity:.85, marginTop: 4, whiteSpace:'pre-wrap' }}>{detail}</div> : null}
              </div>
            </>
          )}
          {isError && (
            <>
              <FaTimesCircle size={20} style={{ color: '#d64545' }}/>
              <div>
                <div style={{ fontWeight: 600 }}>Connection Failed</div>
                {detail ? <div style={{ opacity:.85, marginTop: 4, whiteSpace:'pre-wrap' }}>{detail}</div> : null}
              </div>
            </>
          )}
        </div>

        <div className="ship-modal-actions">
          <button className="btn" onClick={onClose} disabled={isLoading}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ========================= Create/Edit Modal ========================= */
const SERVICE_OPTIONS = ['UPS', 'FedEx', 'eHub', 'ShipStation', 'Stamps.com', 'ShipEngine', 'Unishippers'];

function IntegrationModal({ open, onClose, onSave, initial }) {
  const isEdit = !!initial?.id;

  const [enabled, setEnabled] = useState(initial?.enabled ? 1 : 0);
  const [displayname, setDisplayname] = useState(initial?.displayname || '');
  const [servicetype, setServicetype] = useState(initial?.servicetype || 'UPS');
  const [methodAbbreviations, setMethodAbbreviations] = useState(initial?.method_abbreviations || '');
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodModalSelection, setMethodModalSelection] = useState(new Set());
  useEffect(() => {
    if (!methodModalOpen) return;
    setMethodModalSelection(new Set(normalizeAbbreviationList(methodAbbreviations)));
  }, [methodModalOpen, methodAbbreviations]);
  const [environment, setEnvironment] = useState(initial?.environment || 'sandbox');

  // Credentials (prod)
  const [clientid, setClientId] = useState(initial?.clientid || '');
  const [clientsecret, setClientSecret] = useState(initial?.clientsecret || '');
  const [accountnumber, setAccountNumber] = useState(initial?.accountnumber || '');
  const [accountname, setAccountName] = useState(initial?.accountname || '');

  // URLs (prod)
  const [url, setUrl] = useState(initial?.url || '');
  const [securityurl, setSecurityUrl] = useState(initial?.securityurl || '');

  // Sandbox overrides
  const [sandboxClientId, setSandboxClientId] = useState(initial?.sandbox_clientid || '');
  const [sandboxUrl, setSandboxUrl] = useState(initial?.sandbox_url || '');
  const [sandboxSecurityUrl, setSandboxSecurityUrl] = useState(initial?.sandbox_securityurl || '');

  // Misc
  const [vendormarkup, setVendorMarkup] = useState(initial?.vendormarkup ?? 0);
  const [juicymarkup, setJuicyMarkup] = useState(initial?.juicymarkup ?? 0);
  const [icon, setIcon] = useState(initial?.icon || '');
  const [callbackUrl, setCallbackUrl] = useState(initial?.callback_url || '');
  const [errors, setErrors] = useState({});
  const [field3, setField3] = useState(initial?.field3 || '');
  const [field4, setField4] = useState(initial?.field4 || '');
  const [field5, setField5] = useState(initial?.field5 || '');

  useEffect(() => {
    if (!open) return;
    setEnabled(initial?.enabled ? 1 : 0);
    setDisplayname(initial?.displayname || '');
    setServicetype(initial?.servicetype || 'UPS');
    setMethodAbbreviations(initial?.method_abbreviations || '');
    setMethodModalSelection(new Set());
    setEnvironment(initial?.environment || 'sandbox');

    setClientId(initial?.clientid || '');
    setClientSecret(initial?.clientsecret || '');
    setSandboxClientId(initial?.sandbox_clientid || '');
    setAccountNumber(initial?.accountnumber || '');
    setAccountName(initial?.accountname || '');

    setUrl(initial?.url || '');
    setSecurityUrl(initial?.securityurl || '');
    setSandboxUrl(initial?.sandbox_url || '');
    setSandboxSecurityUrl(initial?.sandbox_securityurl || '');

    setVendorMarkup(initial?.vendormarkup ?? 0);
    setJuicyMarkup(initial?.juicymarkup ?? 0);
    setIcon(initial?.icon || '');
    setCallbackUrl(initial?.callback_url || '');
    setField3(initial?.field3 || '');
    setField4(initial?.field4 || '');
    setField5(initial?.field5 || '');
    setErrors({});
  }, [open, initial]);

  // Seed sensible defaults for Unishippers sandbox to speed setup.
  useEffect(() => {
    if (!open) return;
    const t = String(servicetype || '').toLowerCase();
    if (!t.includes('unishippers')) return;
    if (String(environment || '').toLowerCase() !== 'sandbox') return;

    // Defaults from WWEX staging examples.
    if (!url.trim() && !sandboxUrl.trim()) {
      setUrl('https://speedship.staging-wwex.com');
    }
    if (!securityurl.trim() && !sandboxSecurityUrl.trim()) {
      setSecurityUrl('https://auth.staging-wwex.com');
    }
    if (!field3.trim()) {
      setField3('staging-wwex-apig');
    }
  }, [open, servicetype, environment, url, sandboxUrl, securityurl, sandboxSecurityUrl, field3]);

  useEffect(() => {
    if (!methodModalOpen) {
      setMethodModalSelection(new Set());
    }
  }, [methodModalOpen]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';//meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Shipping Integrations', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [] );

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!displayname.trim()) e.displayname = 'Display name is required';
    if (!servicetype) e.servicetype = 'Service type is required';

    const isUnishippers = String(servicetype || '').toLowerCase().includes('unishippers');
    if (environment === 'production') {
      if (!url.trim()) e.url = 'API base URL is required';
      if (!securityurl.trim()) e.securityurl = 'Security base URL is required';
      if (!clientid.trim()) e.clientid = 'Client ID is required';
      if (!clientsecret.trim()) e.clientsecret = 'Client Secret is required';
    } else {
      if (!sandboxUrl.trim() && !url.trim()) e.sandbox_url = 'Sandbox URL (or generic URL) is required';
      if (!sandboxSecurityUrl.trim() && !securityurl.trim()) e.sandbox_securityurl = 'Sandbox Security URL (or generic) is required';
      if (!sandboxClientId.trim() && !clientid.trim()) e.sandbox_clientid = 'Sandbox Client ID (or generic) is required';
      if (!clientsecret.trim()) e.clientsecret = 'Client Secret is required';
    }
    if (isUnishippers && !field3.trim()) e.field3 = 'Audience is required for Unishippers';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const toggleMethodSelection = (abbr) => {
    setMethodModalSelection(prev => {
      const next = new Set(prev);
      if (next.has(abbr)) next.delete(abbr);
      else next.add(abbr);
      return next;
    });
  };

  const handleMethodModalAdd = (newMethods = []) => {
    if (!Array.isArray(newMethods) || !newMethods.length) return;
    setMethodAbbreviations(prev => {
      const combined = new Set(normalizeAbbreviationList(prev));
      newMethods.forEach((abbr) => {
        const normalized = String(abbr || '').trim().toUpperCase();
        if (normalized) combined.add(normalized);
      });
      return Array.from(combined).join(';');
    });
    setMethodModalOpen(false);
  };

  const doSave = () => {
    if (!validate()) return;
    onSave({
      id: initial?.id ?? null,
      enabled: Number(enabled) ? 1 : 0,
      displayname: displayname.trim(),
      servicetype,
      method_abbreviations: methodAbbreviations.trim() || null,
      environment, // 'sandbox' | 'production'
      field3: field3.trim() || null,
      field4: field4.trim() || null,
      field5: field5.trim() || null,
      // creds
      clientid: clientid.trim(),
      clientsecret: clientsecret.trim(),
      sandbox_clientid: sandboxClientId.trim(),
      accountnumber: accountnumber.trim(),
      accountname: accountname.trim(),
      // urls
      url: url.trim(),
      securityurl: securityurl.trim(),
      sandbox_url: sandboxUrl.trim(),
      sandbox_securityurl: sandboxSecurityUrl.trim(),
      // extra
      vendormarkup: Number(vendormarkup) || 0,
      juicymarkup: Number(juicymarkup) || 0,
      icon: icon.trim() || null,
      juicy: initial?.juicy ?? 0, // ensure user-created defaults to 0
      // extras (UPS)
      callback_url: servicetype.toLowerCase().includes('ups') ? (callbackUrl.trim() || null) : undefined,
    });
  };

  return (
    <>
      <div className="ship-modal-overlay" onClick={onClose}>
        <div className="ship-modal" onClick={(e) => e.stopPropagation()}>
          <div className="ship-modal-titlebar">
            <h3 className="ship-modal-title">{isEdit ? 'Edit Shipping Integration' : 'Add Shipping Integration'}</h3>
            <button className="icon-btn" aria-label="Close" onClick={onClose}>×</button>
          </div>

          <div className="ship-grid">
            <label className="ship-field">
            <span>Display Name</span>
            <input value={displayname} onChange={e => setDisplayname(e.target.value)} className={errors.displayname ? 'field-error' : ''}/>
            {errors.displayname && <span className="ship-error">{errors.displayname}</span>}
          </label>

          <label className="ship-field">
            <span>Service Type</span>
            <select value={servicetype} onChange={e => setServicetype(e.target.value)} className={errors.servicetype ? 'field-error' : ''}>
              {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.servicetype && <span className="ship-error">{errors.servicetype}</span>}
          </label>

          {String(servicetype || '').toLowerCase().includes('unishippers') && (
            <label className="ship-field">
              <span>Audience <FaInfoCircle title="Unishippers OAuth audience (WWEX SpeedShip)" style={{ marginLeft: 6, opacity: 0.85 }} /></span>
              <input
                value={field3}
                onChange={(e) => setField3(e.target.value)}
                placeholder="staging-wwex-apig"
                className={errors.field3 ? 'field-error' : ''}
              />
              {errors.field3 && <span className="ship-error">{errors.field3}</span>}
              <div className="ship-hint">Example sandbox audience: staging-wwex-apig</div>
            </label>
          )}

          <label className="ship-field ship-span-2">
            <span>Method Abbreviations</span>
            <div className="ship-method-input">
              <input
                placeholder="e.g., UPSG;UPS1D;FEXG"
                value={methodAbbreviations}
                onChange={e => setMethodAbbreviations(e.target.value)}
              />
              <button
                type="button"
              className="btn primary ship-method-edit-btn"
              title="Edit standard shipping method abbreviations"
              onClick={() => setMethodModalOpen(true)}
            >
              <FaEdit size={12} />
              Edit
            </button>
            </div>
            <div className="ship-hint">Semicolon-separated carrier/service codes that this integration supports.</div>
          </label>

          <label className="ship-field">
            <span>Environment</span>
            <select value={environment} onChange={e => setEnvironment(e.target.value)}>
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </label>

          <label className="ship-field">
            <span>Enabled</span>
            <select value={enabled} onChange={e => setEnabled(Number(e.target.value))}>
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </label>

          <label className="ship-field">
            <span>Account # (optional)</span>
            <input value={accountnumber} onChange={e => setAccountNumber(e.target.value)} />
          </label>

          <label className="ship-field">
            <span>Account Name (optional)</span>
            <input value={accountname} onChange={e => setAccountName(e.target.value)} />
          </label>

          <label className="ship-field">
            <span>Client ID</span>
            <input value={clientid} onChange={e => setClientId(e.target.value)} className={(environment==='production' && errors.clientid) ? 'field-error' : ''}/>
            {(environment==='production' && errors.clientid) && <span className="ship-error">{errors.clientid}</span>}
          </label>

          <label className="ship-field">
            <span>Client Secret</span>
            <input value={clientsecret} onChange={e => setClientSecret(e.target.value)} className={errors.clientsecret ? 'field-error' : ''}/>
            {errors.clientsecret && <span className="ship-error">{errors.clientsecret}</span>}
          </label>

          <label className="ship-field">
            <span>Sandbox Client ID</span>
            <input value={sandboxClientId} onChange={e => setSandboxClientId(e.target.value)} className={(environment==='sandbox' && errors.sandbox_clientid) ? 'field-error' : ''}/>
            {(environment==='sandbox' && errors.sandbox_clientid) && <span className="ship-error">{errors.sandbox_clientid}</span>}
          </label>

          <label className="ship-field">
            <span>API Base URL</span>
            <input value={url} onChange={e => setUrl(e.target.value)} className={(environment==='production' && errors.url) ? 'field-error' : ''}/>
            {(environment==='production' && errors.url) && <span className="ship-error">{errors.url}</span>}
          </label>

          <label className="ship-field">
            <span>Security Base URL</span>
            <input value={securityurl} onChange={e => setSecurityUrl(e.target.value)} className={(environment==='production' && errors.securityurl) ? 'field-error' : ''}/>
            {(environment==='production' && errors.securityurl) && <span className="ship-error">{errors.securityurl}</span>}
          </label>

          <label className="ship-field">
            <span>Sandbox API Base URL</span>
            <input value={sandboxUrl} onChange={e => setSandboxUrl(e.target.value)} className={(environment==='sandbox' && errors.sandbox_url) ? 'field-error' : ''}/>
            {(environment==='sandbox' && errors.sandbox_url) && <span className="ship-error">{errors.sandbox_url}</span>}
          </label>

          <label className="ship-field">
            <span>Sandbox Security Base URL</span>
            <input value={sandboxSecurityUrl} onChange={e => setSandboxSecurityUrl(e.target.value)} className={(environment==='sandbox' && errors.sandbox_securityurl) ? 'field-error' : ''}/>
            {(environment==='sandbox' && errors.sandbox_securityurl) && <span className="ship-error">{errors.sandbox_securityurl}</span>}
          </label>

          {/* Vendor/Juicy markup fields removed per request */}

          {String(servicetype || '').toLowerCase().includes('ups') && (
            <label className="ship-field ship-span-2">
              <span>API Callback URL (UPS)</span>
              <input
                placeholder="https://yourapp.example.com/api/shipping/ups/auth/callback"
                value={callbackUrl}
                onChange={e => setCallbackUrl(e.target.value)}
              />
              <div className="ship-hint">Optional override for UPS OAuth redirect URI.</div>
            </label>
          )}

          <label className="ship-field ship-span-2">
            <span>Icon (optional, data URL or URL)</span>
            <input value={icon || ''} onChange={e => setIcon(e.target.value)} />
          </label>
        </div>

        <div className="ship-modal-actions">
          <button className="btn" onClick={onClose}><FaTimes /> Cancel</button>
          <button className="btn primary" onClick={doSave}><FaSave /> {isEdit ? 'Save Changes' : 'Save'}</button>
        </div>
      </div>
    </div>
      <MethodAbbrevModal
        open={methodModalOpen}
        reference={METHOD_ABBREV_REFERENCE}
        selected={methodModalSelection}
        onClose={() => setMethodModalOpen(false)}
        onToggle={toggleMethodSelection}
        onAdd={handleMethodModalAdd}
      />
    </>
  );
}

function MethodAbbrevModal({ open, reference = [], selected = new Set(), onClose, onToggle, onAdd }) {
  if (!open) return null;
  const selectedCount = selected?.size || 0;
  return (
    <div className="ship-modal-overlay" onClick={onClose}>
      <div className="ship-method-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ship-modal-titlebar">
          <h3 className="ship-modal-title">Shipping Method Abbreviations</h3>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="ship-method-body">
          <p style={{ marginTop: 0 }}>
            Select the approved shipping method abbreviations to append to this integration.
            You can choose multiple rows and then click “Add selected”.
          </p>
          {reference.map((category) => (
            <div key={category.label} className="ship-method-category">
              <h4>{category.label}</h4>
              <table className="ship-method-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>Use</th>
                    <th>Abbreviation</th>
                    <th>Service</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {category.methods.map(method => {
                    const isChecked = selected?.has(method.abbr);
                    return (
                      <tr key={`${category.label}-${method.abbr}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isChecked || false}
                            onChange={() => onToggle(method.abbr)}
                          />
                        </td>
                        <td><strong>{method.abbr}</strong></td>
                        <td>{method.name}</td>
                        <td>{method.description}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <div className="ship-modal-actions ship-method-actions">
          <div>{selectedCount ? <strong>{selectedCount}</strong> : 'Select codes before adding.'}</div>
          <div>
            <button className="btn" onClick={onClose}>Close</button>
            <button className="btn primary" disabled={!selectedCount} onClick={() => onAdd(Array.from(selected))}>
              Add selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========================= Main ========================= */
export default function ShippingIntegrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState(getInitialSort().field);
  const [sortOrder, setSortOrder] = useState(getInitialSort().order);
  // superuser detection (userid === 1)
  const superUserId = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return Number(u?.userid) || 0;
    } catch { return 0; }
  })();
  const canEditJuicy = superUserId === 1;

  // test modal state
  const [testOpen, setTestOpen] = useState(false);
  const [testStatus, setTestStatus] = useState({ state: 'idle' });
  const [testTitle, setTestTitle] = useState('');
  const [testDetail, setTestDetail] = useState('');

  // create/edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, otherwise row

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/shippingintegrations`);
      const data = await res.json();
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error('Failed to fetch shipping integrations', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  /* ====== Create/Edit wiring ====== */
  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const saveIntegration = async (payload) => {
    try {
      if (payload.id) {
        const res = await fetch(`${API}/api/shippingintegrations/${payload.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error((await res.json().catch(()=>({})))?.error || 'Update failed');
      } else {
        const res = await fetch(`${API}/api/shippingintegrations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error((await res.json().catch(()=>({})))?.error || 'Create failed');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  /* ====== Test modal wiring ====== */
  const openTestModal = (row) => {
    setTestTitle(`Testing ${row.displayname || row.servicetype}`);
    setTestDetail('');
    setTestStatus({ state: 'loading' });
    setTestOpen(true);
  };
  const finishTestSuccess = (detail) => {
    setTestStatus({ state: 'success' });
    setTestDetail(detail || '');
  };
  const finishTestError = (errMsg) => {
    setTestStatus({ state: 'error' });
    setTestDetail(errMsg || 'Unknown error');
  };

  const testAuth = async (row) => {
    const t = String(row.servicetype || '').toLowerCase();
    openTestModal(row);
    try {
      let url = '';
      if (t.includes('ups')) url = `${API}/api/shipping/ups/${row.id}/auth/ping`;
      else if (t.includes('fedex')) url = `${API}/api/shipping/fedex/${row.id}/auth/ping`;
      else if (t.includes('ehub')) url = `${API}/api/shipping/ehub/${row.id}/ping`;
      else if (t.includes('shipstation')) url = `${API}/api/shipping/shipstation/${row.id}/debug`;
      else if (t.includes('stamps')) url = `${API}/api/shipping/stamps/${row.id}/auth/ping`;
      else if (t.includes('shipengine')) url = `${API}/api/shipping/shipengine/${row.id}/test`;
      else if (t.includes('unishippers')) url = `${API}/api/shipping/unishippers/${row.id}/auth/ping`;
      else throw new Error('No test endpoint defined for this provider');

      const res = await fetch(url);
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false) {
        finishTestError(j?.error || j?.message || `HTTP ${res.status}`);
        return;
      }

      if (t.includes('shipengine')) {
        const carrierCount = j.carriers?.length || 0;
        const env = j.isSandbox ? 'Sandbox' : 'Production';
        finishTestSuccess(`Environment: ${env}\nCarriers: ${carrierCount}`);
      } else if (t.includes('ehub')) {
        finishTestSuccess('eHub Ping OK');
      } else if (t.includes('unishippers')) {
        const env = j.environment || 'sandbox';
        const authBase = j.authBase || '—';
        const apiBase = j.apiBase || '—';
        const audience = j.audience || '—';
        finishTestSuccess(`Environment: ${env}\nAuth: ${authBase}\nAPI: ${apiBase}\nAudience: ${audience}`);
      } else if (t.includes('shipstation')) {
        const base = j.base || j.resolvedApiBase || '—';
        const env = j.environment || 'n/a';
        finishTestSuccess(`Base: ${base}\nEnvironment: ${env}`);
      } else {
        finishTestSuccess('Auth OK');
      }
    } catch (e) {
      finishTestError(e.message || String(e));
    }
  };

  const toggleEnabled = async (row, next) => {
    try {
      const res = await fetch(`${API}/api/shippingintegrations/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...row, enabled: next ? 1 : 0 }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Update failed');
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, enabled: next ? 1 : 0 } : r)));
    } catch (e) {
      finishTestError(e.message);
    }
  };

  const onDelete = async (row) => {
    const confirmText = window.prompt(`Type DELETE (all caps) to confirm deleting “…${row.displayname || row.servicetype}”.`);
    if (confirmText !== 'DELETE') return;
    try {
      const res = await fetch(`${API}/api/shippingintegrations/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Delete failed');
      await load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  /* ====== Sorting ====== */
  const sortedNormal = useMemo(() => {
    const f = sortField, o = sortOrder;
    const cmp = (a, b) => {
      const A = (a?.[f] ?? '').toString().toLowerCase();
      const B = (b?.[f] ?? '').toString().toLowerCase();
      if (A > B) return o === 'ASC' ? 1 : -1;
      if (A < B) return o === 'ASC' ? -1 : 1;
      return 0;
    };
    return rows.filter(r => !r.juicy).sort(cmp);
  }, [rows, sortField, sortOrder]);

  const juicyRows = useMemo(() => rows.filter(r => r.juicy).sort((a, b) =>
    (a.displayname || '').localeCompare(b.displayname || '')
  ), [rows]);

  const handleSort = (field) => {
    if (sortField === field) {
      const next = sortOrder === 'ASC' ? 'DESC' : 'ASC';
      setSortOrder(next);
      try { localStorage.setItem(LS_SORT_KEY, JSON.stringify({ field, order: next })); } catch {}
    } else {
      setSortField(field);
      setSortOrder('ASC');
      try { localStorage.setItem(LS_SORT_KEY, JSON.stringify({ field, order: 'ASC' })); } catch {}
    }
  };

  const renderHost = (r) => {
    const host = (r.environment === 'sandbox' ? (r.sandbox_url || r.url) : (r.url || r.sandbox_url)) || '';
    return (host || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');
  };

  return (
    <div className="ship-wrapper">
      <div className="ship-header">
        <button className="btn primary" onClick={openNew}>
          <FaPlus /> New
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <>
          {juicyRows.length > 0 && (
            <div>
              <h2>Special Juicy Integrations</h2>
              <table className="ship-table ship-table--skinned">
                <colgroup>
                  <col className="col-actions" />
                  <col className="col-enabled" />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th className="col-actions">Actions</th>
                    <th className="col-enabled">Enabled</th>
                    <th>Display Name</th>
                    <th>Service</th>
                    <th>Env</th>
                  </tr>
                </thead>
                <tbody>
                  {juicyRows.map(r => (
                    <tr key={r.id}>
                      <td className="ship-actions col-actions">
                        <button className="mini-btn" title="Test Connection" onClick={() => testAuth(r)}>
                          <FaPlug size={14} />
                        </button>
                        {canEditJuicy && (
                          <button className="mini-btn" title="Edit" onClick={() => openEdit(r)}>
                            <FaEdit size={14} />
                          </button>
                        )}
                      </td>
                      <td className="col-enabled">
                        <input
                          type="checkbox"
                          checked={!!r.enabled}
                          onChange={(e) => toggleEnabled(r, e.target.checked)}
                        />
                      </td>
                      <td><BrandIcon type={r.servicetype} /> {r.displayname}</td>
                      <td>{r.servicetype}</td>
                      <td>{r.environment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: '2rem' }}>
            <h2>User Configurable Integrations</h2>
            <table className="ship-table ship-table--skinned">
              <colgroup>
                <col className="col-actions" />
                <col className="col-enabled" />
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-actions">Actions</th>
                  <th className="col-enabled">Enabled</th>
                  <th onClick={() => handleSort('displayname')} className={sortField === 'displayname' ? 'active-sort' : ''}>
                    Display Name
                  </th>
                  <th onClick={() => handleSort('servicetype')} className={sortField === 'servicetype' ? 'active-sort' : ''}>
                    Service
                  </th>
                  <th onClick={() => handleSort('environment')} className={sortField === 'environment' ? 'active-sort' : ''}>
                    Env
                  </th>
                  <th>Account #</th>
                  <th>API Host</th>
                </tr>
              </thead>
              <tbody>
                {sortedNormal.map(r => (
                  <tr key={r.id}>
                    <td className="ship-actions col-actions">
                      <button className="mini-btn" title="Test Connection" onClick={() => testAuth(r)}>
                        <FaPlug size={14} />
                      </button>
                      <button className="mini-btn" title="View" onClick={() => window.alert(JSON.stringify(r, null, 2))}>
                        <FaEye size={14} />
                      </button>
                      <button className="mini-btn" title="Edit" onClick={() => openEdit(r)}>
                        <FaEdit size={14} />
                      </button>
                      <button className="mini-btn danger" title="Delete" onClick={() => onDelete(r)}>
                        <FaTrash size={14} />
                      </button>
                    </td>
                    <td className="col-enabled">
                      <input
                        type="checkbox"
                        checked={!!r.enabled}
                        onChange={(e) => toggleEnabled(r, e.target.checked)}
                      />
                    </td>
                    <td><BrandIcon type={r.servicetype} /> {r.displayname}</td>
                    <td>{r.servicetype}</td>
                    <td>{r.environment}</td>
                    <td>{r.accountnumber || ''}</td>
                    <td>{renderHost(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Test modal */}
      <TestConnectionModal
        open={testOpen}
        onClose={() => { setTestOpen(false); setTestStatus({ state: 'idle' }); setTestDetail(''); }}
        status={testStatus}
        title={testTitle}
        detail={testDetail}
      />

      {/* Create/Edit modal */}
      <IntegrationModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={saveIntegration}
        initial={editing}
      />
    </div>
  );
}
